import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider, ServerClockProvider, SnackbarProvider, Telemetry, ThemeModeProvider } from '@jupi/ui'
import '@jupi/ui/i18n'
// Every mdi icon, bundled: the CSP blocks Iconify's API (packages/ui/vite/mdiIcons.ts).
import 'virtual:mdi-icons'
// Brand fonts, self-hosted (the CSP blocks Google Fonts).
import '@jupi/ui/fonts.css'
import App from './App'
import './calendar.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Members refresh the booking calendar constantly at the airfield on
      // a patchy connection; a short stale window keeps that cheap without
      // showing yesterday's bookings.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // apiFetch already retries once through a token refresh on 401, and
      // a 4xx will not become a 2xx by asking again.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status
        if (status && status < 500) return false
        return failureCount < 2
      },
      // Long enough for a persisted flight plan (below) to still be in
      // the cache when it is restored the next morning.
      gcTime: 24 * 60 * 60_000,
    },
  },
})

// The flight planner's reads (every query key starting 'plan') are kept in
// localStorage, so the plan, its OFP and the waypoints stay readable at an
// airfield with no signal. Nothing else is: bookings must never be shown
// from yesterday's cache. AppShell clears it all on sign-out, so the next
// member on a shared device never sees this one's plan.
const persister = createSyncStoragePersister({
  storage: typeof window === 'undefined' ? undefined : window.localStorage,
  key: 'jupi.plan.cache',
  throttleTime: 2000,
})
const persistOptions = {
  persister,
  maxAge: 3 * 24 * 60 * 60_000,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string } }) =>
      query.queryKey[0] === 'plan' && query.state.status === 'success',
  },
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeModeProvider>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <BrowserRouter>
          <AuthProvider>
            <Telemetry app="web" />
            <SnackbarProvider>
              {/* Outside App so the clock keeps ticking (and the club
                  timezone stays current) across every route. */}
              <ServerClockProvider>
                <App />
              </ServerClockProvider>
            </SnackbarProvider>
          </AuthProvider>
        </BrowserRouter>
      </PersistQueryClientProvider>
    </ThemeModeProvider>
  </StrictMode>,
)
